/**
 * Content Management → Content Tree Library
 *
 * Hierarchical view of every approved syllabus, grouped
 * Board → Class → Subject → Chapter. For each chapter, a row of 5 small
 * status chips tells the admin at a glance what content is present vs missing:
 *
 *   📘 Syllabus   ✅ present · ⚠ missing
 *   🖼 Infographics   count (0 = missing)
 *   🎬 Cinegraphics   count (0 = missing)
 *   📝 Quiz       ✅ cached · ⚠ missing
 *   🃏 Flashcards ✅ cached · ⚠ missing
 *
 * Source of truth = approved syllabus submissions (per user's decision).
 * Rendered entirely client-side from a single GET /api/content/tree response.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  ChevronRight, ChevronDown, RefreshCw, Search, BookOpen,
  FileText, Image as ImageIcon, Video, ClipboardList, Layers3, Gamepad2,
  AlertTriangle, Filter, Sparkles, GitMerge, X, Loader2, ScrollText, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

function AssetChip({ icon: Icon, label, present, countLabel, ambiguousCount = 0, stale = false }) {
  const hasAmbig = ambiguousCount > 0;
  return (
    <span
      title={
        !present
          ? `${label}: missing`
          : stale
            ? `${label}: cached, but the underlying PDF was replaced — regenerate to refresh`
            : hasAmbig
              ? `${label}: ${countLabel} total · ${ambiguousCount} also matches other chapter${ambiguousCount === 1 ? '' : 's'} (ambiguous)`
              : `${label}: present`
      }
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-heading whitespace-nowrap ${
        !present
          ? 'bg-rose-50 text-rose-600 border border-rose-200 border-dashed'
          : stale
            ? 'bg-amber-100 text-amber-800 border border-amber-300'
            : hasAmbig
              ? 'bg-amber-50 text-amber-800 border border-amber-200'
              : 'bg-emerald-100 text-emerald-700'
      }`}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      {countLabel != null && <span className="font-bold ml-0.5">{countLabel}</span>}
      {stale && (
        <span className="inline-flex items-center gap-0.5 ml-0.5 text-amber-700 font-bold">
          <AlertTriangle className="w-2.5 h-2.5" /> STALE
        </span>
      )}
      {!stale && hasAmbig && (
        <span className="inline-flex items-center gap-0.5 ml-0.5 text-amber-700">
          <AlertTriangle className="w-2.5 h-2.5" />
          <span className="font-bold">{ambiguousCount}</span>
        </span>
      )}
      {!present && <AlertTriangle className="w-2.5 h-2.5 ml-0.5" />}
    </span>
  );
}


/**
 * RegenerateStaleButton (Iter 60 · Feb 2026)
 *
 * Appears inline in a chapter row whenever quiz OR flashcards are marked
 * stale (because the underlying syllabus PDF has been replaced). One
 * click hits POST /api/syllabus/submissions/{id}/regenerate-content
 * which deletes the stale cache rows and enqueues a background prewarm.
 */
function RegenerateStaleButton({ submissionId, topic, onDone }) {
  const [busy, setBusy] = useState(false);
  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await apiPost(`/syllabus/submissions/${submissionId}/regenerate-content`, {});
      toast.success(
        `Regenerating "${topic || 'chapter'}" — deleted ${r.quiz_deleted || 0} quiz + ${r.flashcards_deleted || 0} flashcard cache row(s). Refill runs in the background.`
      );
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.message || 'Regenerate failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={regenerate}
      disabled={busy}
      data-testid={`regenerate-stale-${submissionId}`}
      title="Syllabus PDF was replaced — regenerate quizzes & flashcards from the fresh content"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-heading bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:brightness-110 active:scale-95 transition disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
      Regenerate
    </button>
  );
}


/**
 * MergeSubjectsDialog
 *
 * Lets the admin collapse N near-duplicate subject names (e.g.
 * "Health & Physical Education" vs "Health And Physical Education" vs
 * "Health and Physical Education") into one canonical spelling. Scoped to
 * a specific (board, class) so an accidental merge can't bleed across the
 * entire library. Cascades the rename across syllabus_submissions,
 * chunks, page_images, content_library, quiz_cache, and flashcard_cache —
 * the backend does the heavy lifting in one call.
 */
function MergeSubjectsDialog({ board, classLevel, subjects, onClose, onMerged }) {
  const [selected, setSelected] = useState(() => new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  // When the admin selects exactly 2+ subjects, auto-suggest the first
  // selected one as the canonical target (they can override). Saves a
  // click in the most common case.
  const handleToggle = (subj) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(subj)) next.delete(subj); else next.add(subj);
      // Re-default target to the first remaining selection.
      const list = Array.from(next);
      setTarget(t => list.includes(t) ? t : (list[0] || ''));
      return next;
    });
  };

  const submit = async () => {
    const sources = Array.from(selected);
    const finalTarget = target.trim();
    if (sources.length < 2) {
      toast.error('Pick at least 2 subjects to merge.');
      return;
    }
    if (!finalTarget) {
      toast.error('Type the canonical subject name to merge into.');
      return;
    }
    if (!window.confirm(
      `Merge these ${sources.length} subject${sources.length === 1 ? '' : 's'} into "${finalTarget}"?\n\n` +
      `Cascades the rename across submissions, chunks, page images, infographics, cinegraphics, ` +
      `quizzes, and flashcards. Scoped to ${board.toUpperCase()} · Class ${classLevel}. ` +
      `This rewrites the canonical subject name everywhere — irreversible without a restore.`,
    )) return;
    setBusy(true);
    try {
      const r = await apiPost('/syllabus/subjects/merge', {
        source_subjects: sources,
        target_subject: finalTarget,
        board,
        class_level: String(classLevel),
      });
      const total = Object.values(r?.updated || {}).reduce((a, b) => a + (b > 0 ? b : 0), 0);
      toast.success(
        `Merged ${sources.length} subject${sources.length === 1 ? '' : 's'} into "${finalTarget}" — ` +
        `${total} row${total === 1 ? '' : 's'} updated across all collections.`,
        { duration: 6000 },
      );
      onMerged?.();
    } catch (e) {
      toast.error(e?.message || 'Merge failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      data-testid="merge-subjects-dialog">
      <Card className="w-full max-w-lg p-5 rounded-2xl space-y-4">
        <div className="flex items-start gap-2">
          <GitMerge className="w-5 h-5 text-indigo-500 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-heading text-base font-semibold text-slate-800">Merge subjects</h3>
            <p className="text-[11px] text-slate-500 font-body mt-0.5">
              Collapse near-duplicate spellings under <strong>{board.toUpperCase()} · Class {classLevel}</strong> into one canonical subject. All chapters + assets are re-keyed in one transaction.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="merge-subjects-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider font-heading text-slate-500">Subjects in this class</p>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1 bg-slate-50/40">
            {subjects.length === 0 && (
              <p className="text-[11px] text-slate-400 font-body py-2 text-center">No subjects to merge — there&apos;s only one (or zero) here.</p>
            )}
            {subjects.map(s => {
              const checked = selected.has(s.subject);
              return (
                <label key={s.subject}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs font-body ${
                    checked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-white border border-transparent'
                  }`}
                  data-testid={`merge-subjects-pick-${s.subject}`}>
                  <input type="checkbox"
                    checked={checked}
                    onChange={() => handleToggle(s.subject)}
                    className="rounded text-indigo-600 focus:ring-indigo-300" />
                  <span className="flex-1 text-slate-700">{s.subject}</span>
                  <span className="text-[10px] text-slate-400">{s.chapters.length} ch.</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider font-heading text-slate-500">Merge into (canonical name)</p>
          <Input
            value={target}
            onChange={e => setTarget(e.target.value)}
            placeholder="Type the canonical subject name…"
            className="text-xs h-9 rounded-md border-slate-200"
            data-testid="merge-subjects-target" />
          <p className="text-[10px] text-slate-400 font-body">
            Tip: this can be one of the selected subjects (e.g. the most-correct spelling) or a brand-new name.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose} disabled={busy}
            className="text-xs font-heading text-slate-600"
            data-testid="merge-subjects-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || selected.size < 2 || !target.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs font-heading disabled:opacity-40"
            data-testid="merge-subjects-submit">
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Merging…</>
              : <><GitMerge className="w-3.5 h-3.5 mr-1.5" /> Merge {selected.size || ''} into 1</>
            }
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function ContentMgmtTreeLibrary({ refreshToken }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [expandedBoards, setExpandedBoards] = useState({});  // {board_key: true}
  const [expandedClasses, setExpandedClasses] = useState({}); // {`${board}/${class}`: true}
  const [expandedSubjects, setExpandedSubjects] = useState({}); // {`${board}/${class}/${subject}`: true}
  const [prewarming, setPrewarming] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Non-null → the cost-confirmation dialog is open, holding the dry-run summary.
  const [confirmData, setConfirmData] = useState(null);
  // When non-null = {board, classLevel, subjects[]} → renders the merge dialog
  const [mergeContext, setMergeContext] = useState(null);

  // Step 1: dry-run preview (spends ZERO credits) → show a confirmation popup
  // with the exact number of AI generations before anything runs.
  const openPrewarmConfirm = useCallback(async () => {
    setPreviewing(true);
    try {
      const r = await apiGet('/syllabus/prewarm-missing/preview');
      if (!r || (r.estimated_ai_calls || 0) === 0) {
        toast.success(`Nothing to generate — all ${r?.total_approved ?? 0} chapters are already fully cached.`);
        setPreviewing(false);
        return;
      }
      setConfirmData(r);
    } catch (err) {
      toast.error(err?.message || 'Failed to load preview');
    }
    setPreviewing(false);
  }, []);

  // Step 2: user confirmed → actually run (this is the only call that spends).
  const runPrewarm = useCallback(async () => {
    setConfirmData(null);
    setPrewarming(true);
    try {
      const r = await apiPost('/syllabus/prewarm-missing', {});
      if (r.queued === 0) {
        toast.success(`All ${r.total_approved} chapters already have cached quizzes, flashcards & puzzles.`);
      } else {
        toast.success(
          `Started generating for ${r.queued} chapter${r.queued > 1 ? 's' : ''} (~${r.estimated_ai_calls} AI items). ` +
          `Refresh the tree in a few minutes — chips will flip to green.`,
        );
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to start prewarm');
    }
    setPrewarming(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiGet('/content/tree');
      setTree(r.tree || []);
      // Expand the first board + first class by default so admin sees structure
      if ((r.tree || []).length > 0) {
        const firstBoard = r.tree[0].board;
        setExpandedBoards(s => ({ ...s, [firstBoard]: true }));
        if (r.tree[0].classes?.[0]) {
          const firstClass = r.tree[0].classes[0].class_level;
          setExpandedClasses(s => ({ ...s, [`${firstBoard}/${firstClass}`]: true }));
        }
      }
    } catch {
      setError('Failed to load tree');
      toast.error('Failed to load Content Tree');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshToken !== undefined) load(); }, [refreshToken]); // eslint-disable-line

  // Flat stats across the tree for the summary banner
  const stats = useMemo(() => {
    let chapters = 0, hasInfo = 0, hasCine = 0, hasQuiz = 0, hasFlash = 0, hasPuzzle = 0;
    let orphaned = 0, ambiguous = 0;
    tree.forEach(b => b.classes.forEach(c => c.subjects.forEach(s => {
      orphaned += (s.orphaned_infographic_count || 0) + (s.orphaned_cinegraphic_count || 0);
      ambiguous += (s.ambiguous_infographic_count || 0) + (s.ambiguous_cinegraphic_count || 0);
      s.chapters.forEach(ch => {
        chapters += 1;
        if (ch.infographics_count > 0) hasInfo += 1;
        if (ch.cinegraphics_count > 0) hasCine += 1;
        if (ch.has_quiz) hasQuiz += 1;
        if (ch.has_flashcards) hasFlash += 1;
        if (ch.has_puzzle) hasPuzzle += 1;
      });
    })));
    return { chapters, hasInfo, hasCine, hasQuiz, hasFlash, hasPuzzle, orphaned, ambiguous };
  }, [tree]);

  const filtered = useMemo(() => {
    const qLow = q.trim().toLowerCase();
    // Traverse and drop chapters that don't match search / only-missing filter.
    return tree
      .map(b => ({
        ...b,
        classes: b.classes
          .map(c => ({
            ...c,
            subjects: c.subjects
              .map(s => ({
                ...s,
                chapters: s.chapters.filter(ch => {
                  if (qLow && !ch.topic.toLowerCase().includes(qLow)
                      && !s.subject.toLowerCase().includes(qLow)) return false;
                  if (onlyMissing) {
                    // "Missing" = at least one asset kind missing
                    const isFullyLoaded =
                      ch.has_syllabus &&
                      ch.infographics_count > 0 &&
                      ch.cinegraphics_count > 0 &&
                      ch.has_quiz &&
                      ch.has_flashcards;
                    if (isFullyLoaded) return false;
                  }
                  return true;
                }),
              }))
              .filter(s => s.chapters.length > 0),
          }))
          .filter(c => c.subjects.length > 0),
      }))
      .filter(b => b.classes.length > 0);
  }, [tree, q, onlyMissing]);

  const toggleBoard = (key) => setExpandedBoards(s => ({ ...s, [key]: !s[key] }));
  const toggleClass = (key) => setExpandedClasses(s => ({ ...s, [key]: !s[key] }));
  const toggleSubject = (key) => setExpandedSubjects(s => ({ ...s, [key]: !s[key] }));

  const expandAll = () => {
    const b = {}, c = {}, s = {};
    tree.forEach(bd => {
      b[bd.board] = true;
      bd.classes.forEach(cl => {
        c[`${bd.board}/${cl.class_level}`] = true;
        cl.subjects.forEach(sj => {
          s[`${bd.board}/${cl.class_level}/${sj.subject}`] = true;
        });
      });
    });
    setExpandedBoards(b); setExpandedClasses(c); setExpandedSubjects(s);
  };
  const collapseAll = () => {
    setExpandedBoards({}); setExpandedClasses({}); setExpandedSubjects({});
  };

  return (
    <div className="space-y-4" data-testid="cm-content-tree">
      <div className="flex items-start gap-2">
        <Layers3 className="w-5 h-5 text-indigo-500 mt-0.5" />
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Content Tree</h3>
          <p className="text-[11px] text-slate-500 font-body mt-0.5">
            Approved syllabus submissions define the chapters Gurukul Veda knows about. For each chapter, see which assets (infographics, cinegraphics, quizzes, flashcards, puzzles) are ready and which are missing — so you can plan targeted uploads.
          </p>
        </div>
        <Button onClick={openPrewarmConfirm} disabled={prewarming || previewing || loading} variant="ghost"
          className="ml-auto text-xs font-heading text-amber-700 hover:bg-amber-50"
          data-testid="cm-tree-prewarm-missing"
          title="Generate quizzes, flashcards & puzzles for any chapter whose cache is still empty">
          <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${(prewarming || previewing) ? 'animate-pulse' : ''}`} />
          {previewing ? 'Checking…' : prewarming ? 'Queuing…' : 'Pre-generate missing'}
        </Button>
        <Button onClick={load} disabled={loading} variant="ghost"
          className="text-xs font-heading text-slate-600"
          data-testid="cm-tree-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary banner — Iter 60m: 6-up grid on md+ (was 5), so Puzzles
          shows alongside the other cached-asset counters. Mobile keeps
          2-up to stay legible. */}
      <Card className="p-3 rounded-2xl border-slate-100" data-testid="cm-tree-summary">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-center">
          <SummaryBox label="Chapters" value={stats.chapters} icon={BookOpen} tone="slate" />
          <SummaryBox label="With infographic" value={`${stats.hasInfo}/${stats.chapters}`} icon={ImageIcon} tone="indigo" />
          <SummaryBox label="With cinegraphic" value={`${stats.hasCine}/${stats.chapters}`} icon={Video} tone="amber" />
          <SummaryBox label="Quiz cached" value={`${stats.hasQuiz}/${stats.chapters}`} icon={ClipboardList} tone="emerald" />
          <SummaryBox label="Flashcards cached" value={`${stats.hasFlash}/${stats.chapters}`} icon={Layers3} tone="rose" />
          <SummaryBox label="Puzzles cached" value={`${stats.hasPuzzle}/${stats.chapters}`} icon={Gamepad2} tone="violet" />
        </div>
        {(stats.orphaned > 0 || stats.ambiguous > 0) && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2 text-[11px] font-body">
            <span className="font-heading uppercase tracking-wider text-slate-500 text-[10px]">Match issues</span>
            {stats.ambiguous > 0 && (
              <span data-testid="cm-tree-stat-ambiguous"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                <AlertTriangle className="w-3 h-3" /> {stats.ambiguous} ambiguous
              </span>
            )}
            {stats.orphaned > 0 && (
              <span data-testid="cm-tree-stat-orphaned"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700">
                <AlertTriangle className="w-3 h-3" /> {stats.orphaned} orphaned
              </span>
            )}
            <span className="text-slate-500">
              Fix in <strong>Content Library</strong> tab — rename the chapter on each row.
            </span>
          </div>
        )}
      </Card>

      {/* Filters */}
      <Card className="p-3 rounded-2xl border-slate-100" data-testid="cm-tree-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search chapter or subject…"
              className="pl-8 text-xs h-9 rounded-full border-slate-200"
              data-testid="cm-tree-search" />
          </div>
          <Button variant={onlyMissing ? 'default' : 'ghost'}
            onClick={() => setOnlyMissing(m => !m)}
            data-testid="cm-tree-only-missing"
            className={`text-xs font-heading rounded-full ${onlyMissing ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'text-slate-600'}`}>
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            {onlyMissing ? 'Showing only gaps' : 'Show only gaps'}
          </Button>
          <Button variant="ghost" onClick={expandAll}
            className="text-xs font-heading text-slate-600"
            data-testid="cm-tree-expand-all">Expand all</Button>
          <Button variant="ghost" onClick={collapseAll}
            className="text-xs font-heading text-slate-600"
            data-testid="cm-tree-collapse-all">Collapse all</Button>
        </div>
      </Card>

      {/* Tree */}
      <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="cm-tree-body">
        {loading ? (
          <div className="py-16 flex items-center justify-center"><RefreshCw className="w-5 h-5 text-indigo-500 animate-spin" /></div>
        ) : error ? (
          <div className="py-16 text-center text-xs text-rose-600 font-body">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <BookOpen className="w-9 h-9 mx-auto text-slate-300 mb-2" />
            <p className="text-xs text-slate-500 font-body">
              {tree.length === 0
                ? 'No approved syllabus yet. Approve syllabus submissions in the "Syllabus Library" tab — chapters show up here automatically.'
                : 'No chapters match these filters.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((b) => {
              const bopen = !!expandedBoards[b.board];
              const boardChapterCount = b.classes.reduce(
                (acc, c) => acc + c.subjects.reduce((a2, s) => a2 + s.chapters.length, 0), 0,
              );
              return (
                <div key={b.board} data-testid={`tree-board-${b.board}`}>
                  <button onClick={() => toggleBoard(b.board)}
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50/70"
                    data-testid={`tree-board-toggle-${b.board}`}>
                    {bopen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px] uppercase">{b.board || '—'}</Badge>
                    <span className="text-xs font-heading text-slate-700">{b.classes.length} classes</span>
                    <span className="text-[10px] text-slate-400 font-body ml-auto">{boardChapterCount} chapters</span>
                  </button>
                  {bopen && b.classes.map(c => {
                    const ckey = `${b.board}/${c.class_level}`;
                    const copen = !!expandedClasses[ckey];
                    const classChapters = c.subjects.reduce((a, s) => a + s.chapters.length, 0);
                    return (
                      <div key={ckey} className="pl-6 border-l-2 border-slate-50" data-testid={`tree-class-${ckey}`}>
                        <div className="flex items-center gap-1 hover:bg-slate-50/40">
                          <button onClick={() => toggleClass(ckey)}
                            className="flex-1 flex items-center gap-2 px-4 py-2 text-left"
                            data-testid={`tree-class-toggle-${ckey}`}>
                            {copen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            <Badge className="bg-slate-100 text-slate-700 rounded-full text-[10px]">Class {c.class_level}</Badge>
                            <span className="text-xs font-heading text-slate-600">{c.subjects.length} subjects</span>
                            <span className="text-[10px] text-slate-400 font-body ml-auto">{classChapters} chapters</span>
                          </button>
                          {/* Merge subjects — only useful when the class has ≥2 distinct subject spellings */}
                          {c.subjects.length >= 2 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMergeContext({ board: b.board, classLevel: c.class_level, subjects: c.subjects });
                              }}
                              title="Merge duplicate / near-duplicate subjects in this class"
                              data-testid={`tree-class-merge-${ckey}`}
                              className="mr-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-heading text-indigo-600 hover:bg-indigo-50">
                              <GitMerge className="w-3 h-3" />
                              Merge subjects
                            </button>
                          )}
                        </div>
                        {copen && c.subjects.map(s => {
                          const skey = `${b.board}/${c.class_level}/${s.subject}`;
                          const sopen = !!expandedSubjects[skey];
                          const orphanTotal = (s.orphaned_infographic_count || 0) + (s.orphaned_cinegraphic_count || 0);
                          const ambigTotal = (s.ambiguous_infographic_count || 0) + (s.ambiguous_cinegraphic_count || 0);
                          return (
                            <div key={skey} className="pl-6 border-l-2 border-slate-50" data-testid={`tree-subj-${skey}`}>
                              <button onClick={() => toggleSubject(skey)}
                                className="w-full flex items-center gap-2 px-4 py-2 hover:bg-slate-50/40"
                                data-testid={`tree-subj-toggle-${skey}`}>
                                {sopen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px]">{s.subject}</Badge>
                                {ambigTotal > 0 && (
                                  <span
                                    title={`${ambigTotal} uploaded asset${ambigTotal === 1 ? '' : 's'} match multiple chapters — rename to disambiguate`}
                                    data-testid={`tree-subj-ambig-${skey}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-heading text-amber-800">
                                    <AlertTriangle className="w-3 h-3" /> {ambigTotal} ambiguous
                                  </span>
                                )}
                                {orphanTotal > 0 && (
                                  <span
                                    title={`${orphanTotal} uploaded asset${orphanTotal === 1 ? '' : 's'} don't match any syllabus chapter here — students can't see them`}
                                    data-testid={`tree-subj-orphaned-${skey}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-[10px] font-heading text-rose-700">
                                    <AlertTriangle className="w-3 h-3" /> {orphanTotal} orphaned
                                  </span>
                                )}
                                <span className="text-[10px] text-slate-400 font-body ml-auto">{s.chapters.length} chapters</span>
                              </button>
                              {sopen && (
                                <div className="pl-8 pb-2">
                                  {s.chapters.map((ch) => {
                                    const isStale = !!(ch.quiz_stale || ch.flashcards_stale || ch.puzzle_stale);
                                    return (
                                    <div key={ch.submission_id}
                                      className="flex flex-wrap items-center gap-2 py-2 px-3 hover:bg-slate-50/50 rounded-md"
                                      data-testid={`tree-chapter-${ch.submission_id}`}>
                                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                      <p className="text-xs font-body text-slate-800 min-w-0 flex-1 truncate" title={ch.topic}>{ch.topic}</p>
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <AssetChip icon={BookOpen} label="Syllabus" present={ch.has_syllabus} />
                                        <AssetChip
                                          icon={ImageIcon}
                                          label="Infographic"
                                          present={ch.infographics_count > 0}
                                          countLabel={ch.infographics_count > 0 ? ch.infographics_count : null}
                                          ambiguousCount={ch.ambiguous_infographics_count || 0}
                                        />
                                        <AssetChip
                                          icon={Video}
                                          label="Cinegraphic"
                                          present={ch.cinegraphics_count > 0}
                                          countLabel={ch.cinegraphics_count > 0 ? ch.cinegraphics_count : null}
                                          ambiguousCount={ch.ambiguous_cinegraphics_count || 0}
                                        />
                                        <AssetChip icon={ClipboardList} label="Quiz" present={ch.has_quiz} stale={!!ch.quiz_stale} />
                                        <AssetChip icon={Layers3} label="Flashcards" present={ch.has_flashcards} stale={!!ch.flashcards_stale} />
                                        {/* Iter 60k — puzzle chip alongside quiz/flashcards. */}
                                        <AssetChip icon={Gamepad2} label="Puzzle" present={!!ch.has_puzzle} stale={!!ch.puzzle_stale} />
                                        {/* Iter 69 — Questions come UP from students (approved Question Bank
                                            papers); Worksheets go DOWN from us, published per chapter. */}
                                        <AssetChip
                                          icon={ScrollText}
                                          label="Questions"
                                          present={(ch.questions_count || 0) > 0}
                                          countLabel={ch.questions_count > 0 ? ch.questions_count : null}
                                        />
                                        <AssetChip
                                          icon={ClipboardCheck}
                                          label="Worksheet"
                                          present={(ch.worksheets_count || 0) > 0}
                                          countLabel={ch.worksheets_count > 0 ? ch.worksheets_count : null}
                                        />
                                        {isStale && (
                                          <RegenerateStaleButton
                                            submissionId={ch.submission_id}
                                            topic={ch.topic}
                                            onDone={load}
                                          />
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {mergeContext && (
        <MergeSubjectsDialog
          board={mergeContext.board}
          classLevel={mergeContext.classLevel}
          subjects={mergeContext.subjects}
          onClose={() => setMergeContext(null)}
          onMerged={() => { setMergeContext(null); load(); }}
        />
      )}

      <AlertDialog open={!!confirmData} onOpenChange={(o) => { if (!o) setConfirmData(null); }}>
        <AlertDialogContent data-testid="prewarm-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Generate missing content?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  This will make <strong className="text-slate-900">{confirmData?.estimated_ai_calls ?? 0} AI generations</strong>{' '}
                  across <strong className="text-slate-900">{confirmData?.queued ?? 0} chapter{(confirmData?.queued ?? 0) === 1 ? '' : 's'}</strong>.
                  Each AI generation uses credits. Already-cached content is skipped, so nothing is re-generated.
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 divide-y divide-slate-200 text-[13px]">
                  <div className="flex items-center justify-between px-3 py-2" data-testid="prewarm-confirm-puzzles">
                    <span className="flex items-center gap-2 text-slate-700"><Gamepad2 className="w-4 h-4 text-violet-500" /> Puzzles <span className="text-[11px] text-slate-400">(Easy)</span></span>
                    <span className="font-heading text-slate-900">{confirmData?.breakdown?.puzzle_calls ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="flex items-center gap-2 text-slate-700"><ClipboardList className="w-4 h-4 text-emerald-500" /> Quizzes</span>
                    <span className="font-heading text-slate-900">{confirmData?.breakdown?.quiz_calls ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="flex items-center gap-2 text-slate-700"><Layers3 className="w-4 h-4 text-rose-500" /> Flashcard sets</span>
                    <span className="font-heading text-slate-900">{confirmData?.breakdown?.flashcard_calls ?? 0}</span>
                  </div>
                </div>

                {(confirmData?.puzzle_by_subject?.some?.((s) => s.missing_easy > 0)) && (
                  <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-2.5" data-testid="prewarm-confirm-by-subject">
                    <p className="text-[11px] font-heading font-semibold text-violet-800 mb-1.5 flex items-center gap-1.5">
                      <Gamepad2 className="w-3.5 h-3.5" /> Puzzles to generate — by subject (Easy, 1 per chapter)
                    </p>
                    <div className="max-h-40 overflow-y-auto divide-y divide-violet-100">
                      {confirmData.puzzle_by_subject.filter((s) => s.missing_easy > 0).map((s) => (
                        <div key={s.subject} className="flex items-center justify-between py-1 text-[12px]">
                          <span className="text-slate-700">{s.subject}</span>
                          <span className="text-slate-500">
                            <span className="font-heading text-violet-700">{s.missing_easy}</span>
                            <span className="text-slate-400"> / {s.eligible_chapters} chapters</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {confirmData?.puzzle_difficulty_coverage && (
                  <div className="text-[11px] text-slate-500" data-testid="prewarm-confirm-difficulty-coverage">
                    <span className="font-heading text-slate-600">Current puzzle coverage across {confirmData.eligible_puzzle_chapters} eligible chapters:</span>{' '}
                    Easy {confirmData.puzzle_difficulty_coverage.easy.cached}/{confirmData.puzzle_difficulty_coverage.easy.total} ·
                    Medium {confirmData.puzzle_difficulty_coverage.medium.cached}/{confirmData.puzzle_difficulty_coverage.medium.total} ·
                    Hard {confirmData.puzzle_difficulty_coverage.hard.cached}/{confirmData.puzzle_difficulty_coverage.hard.total}.
                    <span className="text-slate-400"> Medium &amp; Hard fill automatically the first time a student plays them (then cached for everyone).</span>
                  </div>
                )}
                <p className="text-[11px] text-slate-400">
                  Runs in the background. Refresh the tree in a few minutes to watch the chips flip to green.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="prewarm-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runPrewarm} data-testid="prewarm-confirm-run"
              className="bg-amber-600 hover:bg-amber-700">
              Yes, generate {confirmData?.estimated_ai_calls ?? 0} items
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryBox({ label, value, icon: Icon, tone }) {
  const map = {
    slate: 'bg-slate-50 text-slate-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
    violet: 'bg-violet-50 text-violet-700',
  };
  return (
    <div className={`rounded-xl p-2.5 ${map[tone]}`}>
      <div className="flex items-center justify-center gap-1 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[9px] font-heading uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-display text-lg font-bold">{value}</p>
    </div>
  );
}
